/**
 * Shared styles + helpers for the offer-approval "Scorecard History" table.
 *
 * Used by both:
 * - the tiptap node in the offer composer (`ScorecardTableExtension`), and
 * - the React renderer used by `HtmlRenderer` (`ScorecardHistoryTable`).
 *
 * Kept here so the two stay visually identical and there's a single place to
 * tune dimensions / borders / rating formatting.
 */

import type { CSSProperties } from 'react';

// Stored on the outer wrapper div by `generateScorecardTableHTML`; the
// HtmlRenderer Interweave `transform` looks for it and hydrates the static
// markup back into the `ScorecardHistoryTable` React component.
export const SCORECARDS_DATA_ATTR = 'data-scorecards-payload';

export interface ScorecardHistoryRow {
  reviewer: string;
  rating: string;
  activity: string;
  scorecard_url: string | null;
}

// Scroll viewport — clips to the available width; the card scrolls inside it.
export const tableScrollStyle: CSSProperties = {
  overflowX: 'auto',
  maxWidth: '100%',
  marginTop: '8px',
  marginBottom: '8px',
};

// Bordered card. inline-block + a fixed min-width so it can exceed a narrow
// viewport (and scroll) while the border always encloses every column. In
// wide containers it sizes to its content.
export const tableCardStyle: CSSProperties = {
  display: 'inline-block',
  verticalAlign: 'top',
  // Wide enough for "Interviewer/Reviewer" / "Scorecard URL" headers to sit
  // on one line; narrower viewports scroll horizontally instead.
  minWidth: '720px',
  boxSizing: 'border-box',
  border: '1px solid #E9EBF4',
  borderRadius: '4px',
};

export const tableCardHeaderStyle: CSSProperties = {
  fontFamily: 'Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 550,
  color: '#413B74',
  backgroundColor: '#F8F9FC',
  padding: '10px 12px',
};

export const tableBaseStyle: CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
};

const thTdStyle: CSSProperties = {
  border: '1px solid #E9EBF4',
  padding: '10px',
  textAlign: 'left',
  fontSize: '14px',
  overflowWrap: 'anywhere',
};

const thStyle: CSSProperties = {
  ...thTdStyle,
  color: '#413B74',
  fontWeight: 550,
  whiteSpace: 'nowrap',
};

// Inner cell borders are removed at the table edges because the outer card
// already draws a border there — avoids doubling-up at the corners.
export const getCellStyle = (
  isFirstColumn: boolean,
  isLastColumn: boolean,
  isLastRow = false
): CSSProperties => ({
  ...thTdStyle,
  borderLeft: isFirstColumn ? 'none' : '1px solid #E9EBF4',
  borderRight: isLastColumn ? 'none' : '1px solid #E9EBF4',
  borderBottom: isLastRow ? 'none' : '1px solid #E9EBF4',
});

export const getHeaderStyle = (
  isFirstColumn: boolean,
  isLastColumn: boolean
): CSSProperties => ({
  ...thStyle,
  borderLeft: isFirstColumn ? 'none' : '1px solid #E9EBF4',
  borderRight: isLastColumn ? 'none' : '1px solid #E9EBF4',
});

export const formatRating = (rating?: string | null): string =>
  rating
    ? rating.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    : '-';
